/**
 * Documents grouped by category (`LIB_DS`) — the « Documents » tab of a
 * contract, claim or vehicle (fiches 22723 / 22744 / 22778).
 *
 * - Categories keep the API order of their first document; documents keep
 *   the API order inside their category. A `null` category is a group with
 *   an empty label (« ne rien afficher et conserver la structure »).
 * - One design-system `Card` per category, its documents in a `List`.
 * - Each document renders from declarative item descriptors with stable keys
 *   `nom` (first visible = primary line) and `date` (secondary line); labels
 *   and tooltips live in `DOCUMENT_ITEM_FIELDS` (`./documentItems.ts`) and
 *   are overridable through `_layoutOverrides` (`items` / `fields` bucket:
 *   rename / hide / reorder / tooltip; a descriptor with `showLabel` prefixes
 *   its value with its label). The title follows `setTitle`.
 * - Publishes its base config under `componentId`.
 *
 * NOT registered by itself — registered per usage
 * (`extranet.contractDocumentsCard`, `extranet.claimDocumentsCard`,
 * `extranet.vehicleDocumentsCard`). No download action (unspecified).
 */

import { useMemo } from 'react';
import { FolderOpen, type LucideIcon } from 'lucide-react';

import { List } from '@components/base/data';
import { Text, Tooltip } from '@components/base/display';
import { Card, VStack } from '@components/base/layout';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import { CardBodyState } from './CardBodyState';
import { CardTitleRow } from './CardTitleRow';
import {
  DOCUMENT_ITEM_FIELDS,
  documentFieldText,
  groupDocumentsByCategory,
  type DocumentItem,
} from './documentItems';

export type { DocumentItem } from './documentItems';

export interface DocumentsByCategoryCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row. */
  title?: string;
  icon?: LucideIcon;
  documents: readonly DocumentItem[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

function withTooltip(text: string, tooltip: string | undefined) {
  if (!tooltip) return text;
  return (
    <Tooltip content={tooltip}>
      <span>{text}</span>
    </Tooltip>
  );
}

export function DocumentsByCategoryCard({
  componentId,
  title,
  icon = FolderOpen,
  documents,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<DocumentsByCategoryCardProps>) {
  usePublishLayoutConfig(
    componentId,
    // `title ?? ''` keeps the editor's title slot (InfoList host) without a base title.
    buildPublishedConfigFromInfoItems([...DOCUMENT_ITEM_FIELDS], title ?? ''),
  );

  const visibleFields = useMemo(() => {
    const overrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
    return [...applyItemOverrides(DOCUMENT_ITEM_FIELDS, overrides)]
      .filter((field) => field.visible !== false)
      .sort(
        (a, b) =>
          (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY),
      );
  }, [_layoutOverrides]);

  const groups = useMemo(() => groupDocumentsByCategory(documents), [documents]);
  const resolvedTitle = resolveCardTitle(title ?? '', _layoutOverrides);
  const [primary, ...secondary] = visibleFields;

  return (
    <VStack gap={4} data-testid="documents-by-category">
      {resolvedTitle !== '' && <CardTitleRow title={resolvedTitle} icon={icon} />}
      <CardBodyState loading={loading} error={error} empty={documents.length === 0}>
        {groups.map((group) => (
          <Card key={group.key} padding="none" data-category={group.key}>
            <Card.Header>
              <Text as="span" size="xs" weight="medium" color="muted">
                {group.label}
              </Text>
            </Card.Header>
            <Card.Content>
              <List dividers>
                {group.documents.map((document) => (
                  <List.Item key={document.id}>
                    <VStack gap={0}>
                      {primary && (
                        <Text as="span" size="sm" weight="medium" truncate>
                          {withTooltip(documentFieldText(primary, document), primary.tooltip)}
                        </Text>
                      )}
                      {secondary.map((field) => (
                        <Text key={field.key} as="span" size="xs" color="muted">
                          {withTooltip(documentFieldText(field, document), field.tooltip)}
                        </Text>
                      ))}
                    </VStack>
                  </List.Item>
                ))}
              </List>
            </Card.Content>
          </Card>
        ))}
      </CardBodyState>
    </VStack>
  );
}
